import React from 'react';

// Utils
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import {
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';

/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnumMaybe is used if schemaType is 'multi-enum'
 * - SectionTextMaybe is used if schemaType is 'text'
 *
 * "disenador_nombre" is handled separately below: unlike other text fields, it should only be
 * shown when "disenador" is checked (si) and the name itself is non-empty.
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };

  const disenadorNombreConfig = listingFieldConfigs?.find(fc => fc?.key === 'disenador_nombre');
  const otherFieldConfigs = listingFieldConfigs?.filter(fc => fc?.key !== 'disenador_nombre');

  const disenadorNombreValue = publicData?.disenador_nombre;
  const isValidDisenadorNombre =
    typeof disenadorNombreValue === 'string' && disenadorNombreValue.trim().length > 0;
  const showDisenadorNombre =
    publicData?.disenador === 'si' &&
    isValidDisenadorNombre &&
    !!disenadorNombreConfig &&
    isFieldForListingType(publicData?.listingType, disenadorNombreConfig) &&
    isFieldForSelectedCategories(disenadorNombreConfig);

  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      otherFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  return (
    <>
      <SectionDetailsMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} />
      {showDisenadorNombre ? (
        <SectionTextMaybe
          text={disenadorNombreValue.trim()}
          heading={disenadorNombreConfig?.showConfig?.label}
        />
      ) : null}
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export default CustomListingFields;
